import type { TFunction } from 'i18next'
import { dayMonthTime, time } from '@/lib/format'
import type { Contact } from '@/services/ayuda'
import type { GuestRequest, GuestRequestStatus } from '@/services/peticiones'
import type { Stay } from '@/services/reserva'
import type { IconName, StatusTone } from '@/ds'
import '../Pagos/Pagos.css'
import './Ayuda.css'

/** Rutas de Ayuda (inventario D16). */
export const HELP_ROUTES = {
  home: '/ayuda',
  newRequest: '/ayuda/peticiones/nueva',
  request: (id: string) => `/ayuda/peticiones/${encodeURIComponent(id)}`,
  conversation: '/ayuda/conversacion',
} as const

export const STATUS_CHIP: Record<GuestRequestStatus, { tone: StatusTone; icon: IconName }> = {
  received: { tone: 'info', icon: 'info' },
  progress: { tone: 'warning', icon: 'clock' },
  resolved: { tone: 'success', icon: 'check' },
}

/** Título de la petición: el que da la propiedad o, si no, su tipo. */
export function requestTitle(r: GuestRequest, t: TFunction): string {
  if (r.title) return r.title
  if (r.type === 'booking_change') return r.changeType ? t(`v2.reserva.peticiones.tipo.${r.changeType}`) : t('v2.reserva.peticiones.tipo.generica')
  return t(`v2.peticiones.tipo.${r.type}`)
}

const sameDay = (a: Date, b: Date) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()

/** "Hoy, 09:12", "Ayer, 18:40" o "4 oct, 18:22" (PantallaAyuda.dc.html). */
export function whenText(iso: string, now: Date, lang: string, t: TFunction): string {
  const d = new Date(iso)
  const yesterday = new Date(now)
  yesterday.setDate(now.getDate() - 1)
  if (sameDay(d, now)) return t('v2.peticiones.lista.hoy', { time: time(iso, lang) })
  if (sameDay(d, yesterday)) return t('v2.peticiones.lista.ayer', { time: time(iso, lang) })
  return t('v2.peticiones.lista.fecha', { date: dayMonthTime(iso, lang) })
}

/** "hace 20 min" en la última hora; si no, "ayer, 20:05". */
export function updatedText(iso: string, now: Date, lang: string, t: TFunction): string {
  const minutes = Math.round((now.getTime() - new Date(iso).getTime()) / 60000)
  if (minutes >= 0 && minutes < 60) return t('v2.peticiones.lista.hace', { count: Math.max(1, minutes) })
  const text = whenText(iso, now, lang, t)
  return text.charAt(0).toLocaleLowerCase(lang) + text.slice(1)
}

/** "Hoy, 09:12 · te las llevamos antes de las 13:00" */
export function requestMeta(r: GuestRequest, now: Date, lang: string, t: TFunction): string {
  const when = whenText(r.createdAt, now, lang, t)
  return r.statusNote ? t('v2.peticiones.lista.conNota', { when, note: r.statusNote }) : when
}

/** Contacto con la API actual, ya leído con la reserva: sirve también sin conexión y antes de cargar Ayuda. */
export function contactOf(stay: Stay): Contact {
  return { whatsapp: stay.contact.whatsapp, email: null, phone: stay.contact.supportPhone, emergencyPhone: stay.contact.emergencyPhone, reference: stay.reference }
}
